import { EventAggregator } from 'aurelia-event-aggregator';
import {
	autoinject,
	bindable,
	computedFrom,
	containerless,
	TaskQueue,
} from 'aurelia-framework';
import { I18N } from 'aurelia-i18n';

import {
	AcurityTable,
	TableColumnDefinition,
} from '~/components/acurity-table/acurity-table';
import { Datepicker } from '~/components/field/common/datepicker';
import { FilterSlideout } from '~/components/slideout/filter-slideout';
import { ShortcutKey } from '~/constants';
import { CodeAndDescription } from '~/models/code-and-description';
import { FormModel } from '~/models/form-model';
import { focusable } from '~/utils/focus';
import { KeyboardShortcut } from '~/utils/keyboard-shortcut';
import { Logger } from '~/utils/logger';
import {
	SearchFieldModel,
	SearchFieldModelMap,
	SearchType,
} from '../field/common/search-definition';

export interface FilterOptionsModel {
	tableSearchFieldModel: SearchFieldModel;
	modifiedSearchFieldModel: SearchFieldModel;
	modifiedUpperSearchFieldModel: SearchFieldModel;
}

const TABLE_FILTER_FIELD_NAME_ENDING = 'z_Table_Name';

@focusable
@containerless
@autoinject
export class Filter {
	/**
	 * Event IDs
	 */
	public static readonly Events = {
		CloseFilterSlideout: 'Filter.CloseFilterSlideout',
		OpenFilterSlideout: 'Filter.OpenFilterSlideout',
		RefreshFilter: 'Filter.RefreshFilter',
		TriggerFilterSlideout: 'Filter.TriggerFilterSlideout',
	};

	@bindable public formModel: FormModel;
	@bindable public recordSelectorTable: AcurityTable;
	@bindable public inputMasks: { [name: string]: string };
	@bindable public searchFieldModelMap: SearchFieldModelMap;
	public canClick: boolean = true;
	public isFilterOpen: boolean = false;
	public readonly nodeId: string = 'a-filter-slideout';

	private openedKeyboardContext = {};

	/**
	 * The 'Apply Filter' button
	 */
	private applyFilterBtnElem: HTMLButtonElement;
	/**
	 * Force disable the slideout
	 */
	private disabled: boolean = false;
	/**
	 * The slideout component
	 */
	private slideout: FilterSlideout;

	private filtersApplied: number = 0;

	/**
	 * Used by the filter-option view.
	 */
	private filterOptionsModel: FilterOptionsModel = {
		tableSearchFieldModel: {
			name: '',
			searchType: SearchType.RECORD_AUTHORISATION_TABLE_NAME,
			visible: true,
			required: false,
		},
		modifiedSearchFieldModel: {
			name: '',
			searchType: SearchType.USER,
			visible: true,
			required: false,
		},
		modifiedUpperSearchFieldModel: {
			name: '',
			searchType: SearchType.USER,
			visible: true,
			required: false,
		},
	};

	// END -----------------------------------------------------------------------------------------

	constructor(
		protected readonly eventAggregator: EventAggregator,
		private readonly datepicker: Datepicker,
		private readonly taskQueue: TaskQueue,
		private readonly i18n: I18N,
	) {}

	public attached() {
		KeyboardShortcut.subscribe(
			this,
			ShortcutKey.ToggleFilter,
			this.slideout.element.querySelectorAll(
				'[click\\.delegate="onClickOpenFilter($event)"]',
			),
		);
		// WEBUI-2869: Filters can be set from tableConfig.tableColumns
		this.updateFilterApplied();
		this.eventAggregator.subscribeOnce(
			AcurityTable.Events.AjaxDataReady,
			(e) => {
				if (e?.table === this.recordSelectorTable) {
					this.updateFilterApplied();
				}
			},
		);
	}

	protected detached(): void {
		KeyboardShortcut.unsubscribeAll(this);
	}

	private onClickOpenFilter(e: MouseEvent | undefined): void {
		if (e) {
			// NOTE: Jake: 2019-07-18 - WEBUI-2245
			// Need to check for "e" as the shortcut keys code
			// can trigger this.
			$(e.currentTarget).tooltip('hide');
		}
		this.toggle();
	}

	private onTransitionEnd = (): void => {
		// NOTE: Jake: 2019-07-18 - WEBUI-2245
		// Unbind after executing as we only want this to fire when the open() panel
		// animation has completed.
		this.slideout.element.removeEventListener(
			'transitionend',
			this.onTransitionEnd,
		);

		// NOTE: Jake: 2019-07-18 - WEBUI-2245
		// Tested this in Chrome + Edge. Changed this code to occur on transitionend so that regardless of lag
		// or timing, the first element will be focused.
		const firstInputElem = this.slideout.element.querySelector<HTMLElement>(
			'input:not([readonly])',
		);
		if (firstInputElem) {
			firstInputElem.focus();
		}
	};

	public setFilter(fieldName: string, value: string | string[]): void {
		for (const tableColumn of this.recordSelectorTable.tableColumnInfo) {
			if (tableColumn.name !== fieldName) {
				continue;
			}
			tableColumn.filter.value = value;
			this.updateFilterApplied();
			return;
		}
		throw new Error(
			fieldName + ' does not exist on this form or could not be found.',
		);
	}

	/**
	 * Toggle the slideout
	 */
	public toggle(): void {
		if (this.slideout.isShown()) {
			this.close();
		} else {
			this.open();
		}
	}

	/**
	 * Close the slideout if it is open, otherwise, does nothing.
	 */
	public close(): void {
		const slideOutIsClosed = !this.slideout.isShown();
		if (this.disabled || slideOutIsClosed) {
			Logger.log(
				'Slideout is',
				this.disabled ? 'disabled' : 'already closed',
			);
		}
		if (slideOutIsClosed) {
			return;
		}

		Logger.log('Closing filter slideout');
		this.slideout.close();

		// Publish events
		this.eventAggregator.publish(Filter.Events.CloseFilterSlideout, {
			source: this.nodeId,
		});

		KeyboardShortcut.unsubscribeAll(this.openedKeyboardContext);
		this.isFilterOpen = false;
	}

	/**
	 * Open the slideout if it is closed, otherwise, does nothing.
	 */
	public open(): void {
		const slideOutIsOpen = this.slideout.isShown();
		if (this.disabled || slideOutIsOpen) {
			Logger.log(
				'Slideout is',
				this.disabled ? 'disabled' : 'already shown',
			);
		}
		if (slideOutIsOpen) {
			return;
		}

		Logger.log('Opening filter slideout');
		this.slideout.open();

		this.slideout.element.addEventListener(
			'transitionend',
			this.onTransitionEnd,
		);

		// Publish events
		this.eventAggregator.publish(Filter.Events.TriggerFilterSlideout, {
			source: this.nodeId,
		});
		this.eventAggregator.publish(Filter.Events.OpenFilterSlideout);

		KeyboardShortcut.subscribe(
			this.openedKeyboardContext,
			ShortcutKey.Save,
			this.slideout.element.querySelectorAll(
				'[click\\.trigger="filterRefresh()"]',
			),
		);
		KeyboardShortcut.subscribe(
			this.openedKeyboardContext,
			ShortcutKey.ClearFilter,
			this.slideout.element.querySelectorAll(
				'[click\\.trigger="clear()"]',
			),
		);
		KeyboardShortcut.subscribe(
			this.openedKeyboardContext,
			ShortcutKey.Close,
			this.slideout.element.querySelectorAll(
				'[click\\.delegate="close()"]',
			),
		);
		this.isFilterOpen = true;
	}

	/**
	 * Clears filter - resets the filters to initial value then refreshes the data
	 */
	private clear(): void {
		this.recordSelectorTable.clearFilterOptions();
		this.refreshData();
	}

	/**
	 * Refreshes the filter
	 */
	private filterRefresh(): void {
		if (this.slideout.isShown()) {
			this.close();
		}
		this.refreshData();
	}

	/**
	 * This is used by filter-option.html
	 */
	private isColumnVisible(column: TableColumnDefinition): boolean {
		return this.recordSelectorTable.isColumnVisibleInFilter(column);
	}

	/**
	 * Refreshes the data
	 */
	private refreshData(): void {
		if (
			!this.recordSelectorTable ||
			!this.recordSelectorTable.tableColumnInfo
		) {
			Logger.warning('Unable to find table to filter data on');
			return;
		}

		this.updateFilterApplied();
		this.recordSelectorTable.refreshAjaxTable();
		this.eventAggregator.publish(Filter.Events.RefreshFilter, {
			source: this.nodeId,
			payload: this.nodeId,
		});
	}

	/**
	 * updateFilterApplied is used to make the filter button become red when filters are applied.
	 */
	public updateFilterApplied(): void {
		let filtersApplied = 0;
		for (const tableColumn of this.recordSelectorTable.tableColumnInfo) {
			if (
				!this.isColumnVisible(tableColumn) ||
				((!tableColumn.filter.value ||
					tableColumn.filter.value.length === 0) &&
					!tableColumn.filter.upperValue)
			) {
				continue;
			}
			filtersApplied++;
		}
		this.filtersApplied = filtersApplied;
	}

	@computedFrom('i18n')
	get applyFilterLabel(): string {
		return this.i18n.tr('clickable-elements.apply');
	}

	@computedFrom('i18n')
	get clearFilterLabel(): string {
		return this.i18n.tr('clickable-elements.clear-filter');
	}

	/**
	 * Called by the filter-option view.
	 */
	private isTextFilter(
		dataTypeName: string,
		codeList: CodeAndDescription[],
		fieldName: string,
	): boolean {
		return (
			(dataTypeName === 'String' || dataTypeName === 'Character') &&
			!codeList &&
			!this.isModifiedField(fieldName) &&
			!this.isTableFilter(fieldName) &&
			!this.isSearchFilter(fieldName)
		);
	}

	/**
	 * Called by the filter-option view.
	 */
	private isMultiSelectFilter(
		dataTypeName: string,
		codeList: CodeAndDescription[],
	): boolean {
		return (
			(dataTypeName === 'String' || dataTypeName === 'Character') &&
			!!codeList
		);
	}

	/**
	 * Called by the filter-option view.
	 */
	private isNumberFilter(dataTypeName: string, fieldName: string): boolean {
		return (
			(dataTypeName === 'BigDecimal' ||
				dataTypeName === 'Integer' ||
				dataTypeName === 'Long' ||
				dataTypeName === 'Currency' ||
				dataTypeName === 'Percentage') &&
			!this.isModifiedField(fieldName)
		);
	}

	/**
	 * Called by the filter-option view.
	 */
	private isNumberUpperValueFilter(
		dataTypeName: string,
		fieldName: string,
		filterOperation: string,
	): boolean {
		return (
			this.isNumberFilter(dataTypeName, fieldName) &&
			this.isBetweenOperationSelected(filterOperation)
		);
	}

	/**
	 * Called by the filter-option view.
	 */
	private isDatePickerFilter(dataTypeName: string): boolean {
		return dataTypeName === 'Date' || dataTypeName === 'Timestamp';
	}

	/**
	 * Called by the filter-option view.
	 */
	private isDatePickerUpperValueFilter(
		dataTypeName: string,
		filterOperation: string,
	): boolean {
		return (
			this.isDatePickerFilter(dataTypeName) &&
			this.isBetweenOperationSelected(filterOperation)
		);
	}

	/**
	 * Called by the filter-option view.
	 */
	private isModifiedField(fieldName: string): boolean {
		return fieldName.endsWith('n_US_ID_Modified');
	}

	/**
	 * Called by the filter-option view.
	 */
	private isModifiedFieldUpper(
		fieldName: string,
		filterOperation: string,
	): boolean {
		return (
			this.isModifiedField(fieldName) &&
			this.isBetweenOperationSelected(filterOperation)
		);
	}

	/**
	 * Called by the filter-option view.
	 */
	private isTableFilter(fieldName: string): boolean {
		return fieldName.endsWith(TABLE_FILTER_FIELD_NAME_ENDING);
	}

	/**
	 * Called by the filter-option view.
	 */
	private isSearchFilter(fieldName: string): boolean {
		return (
			this.searchFieldModelMap && !!this.searchFieldModelMap[fieldName]
		);
	}

	/**
	 * Called by the filter-option view.
	 */
	private hasIgnoreCaseCheckbox(
		dataTypeName: string,
		fieldName: string,
	): boolean {
		return (
			(dataTypeName === 'String' || dataTypeName === 'Character') &&
			!this.isTableFilter(fieldName) &&
			!this.isSearchFilter(fieldName)
		);
	}

	private isBetweenOperationSelected(filterOperation: string) {
		return filterOperation === 'BETWEEN';
	}
}
